import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import type { AxiosRequestConfig } from 'axios';
import { useHipSpinePreferences } from '@/stores/hipSpinePreferences/preferencesStore';
import { usePreferencesSubject } from '@/stores/preferencesSubject/preferencesSubjectStore';
import {
    type FieldErrorKind,
    validators,
} from '@/views/preferences/hipSpine/hipSpinePreferencesValidation';
import type { HipSpineUserPreferenceData } from '@/stores/hipSpinePreferences/types';

type HipSpineNotificationState = 'success' | 'error' | null;

type HipSpineSaveError = {
    title: string;
    message: string;
};

const BOUND_FIELDS = [
    'maxCupInclinationBound',
    'minCupInclinationBound',
    'maxCupAnteversionBound',
    'minCupAnteversionBound',
] as const;

function validationOf(current: HipSpineUserPreferenceData) {
    function fieldErrorKind(field: string): FieldErrorKind | null {
        if (validators[field] === undefined) {
            throw new Error(`no validator for ${field}`);
        }
        return validators[field](current);
    }

    return {
        fieldErrorKind,
        hasValidationErrors: computed(() =>
            BOUND_FIELDS.some((field) => fieldErrorKind(field) !== null),
        ),
    };
}

export const useHipSpinePreferencesViewModel = defineStore('hip-spine-preferences-view', () => {
    const store = useHipSpinePreferences();
    const subject = usePreferencesSubject();
    const saveSuccess = ref(false);

    watch(
        () => store.hasUnsavedChanges,
        (dirty) => {
            if (dirty) saveSuccess.value = false;
        },
    );

    const { fieldErrorKind, hasValidationErrors } = validationOf(store.current);

    const canSave = computed(
        () => store.hasUnsavedChanges && store.allFieldsFilled && !hasValidationErrors.value,
    );

    const hasError = computed(() => store.isError);

    const notificationState = computed<HipSpineNotificationState>(() => {
        if (saveSuccess.value) return 'success';
        if (hasError.value) return 'error';
        return null;
    });

    const saveError = computed<HipSpineSaveError | null>(() =>
        hasError.value
            ? { title: store.error?.title ?? '', message: store.error?.message ?? '' }
            : null,
    );

    async function load(surgeonId: number, config?: AxiosRequestConfig): Promise<void> {
        await store.load(surgeonId, config);
    }

    async function save(): Promise<void> {
        if (store.isSaving || subject.selectedId === null) return;
        saveSuccess.value = false;
        await store.save(subject.selectedId);
        saveSuccess.value = !store.isError;
    }

    return {
        current: store.current,
        fieldErrorKind,
        hasUnsavedChanges: computed(() => store.hasUnsavedChanges),
        canSave,
        notificationState,
        saveError,
        isReady: computed(() => store.isReady),
        isSaving: computed(() => store.isSaving),
        load,
        save,
    };
});
